import { FileDown } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { ApiError, api, tokens } from '../lib/api';
import { fileSize } from '../lib/samples';
import type { Child, PortfolioPreview, PortfolioSection } from '../lib/types';
import { GradeSelect } from '../ui/GradeSelect';
import { useParent } from './ParentContext';

/**
 * The year-end portfolio (#603): one PDF of a child's year, for your records, an evaluator or an accountability
 * association. Pick the range and the sections; the counts show what each will hold.
 */
const SECTIONS: { id: PortfolioSection; label: string }[] = [
  { id: 'summary', label: 'State requirement summary' },
  { id: 'attendance', label: 'Attendance log' },
  { id: 'lessons', label: 'Subjects and lesson plans' },
  { id: 'grades', label: 'Grades' },
  { id: 'reading', label: 'Reading list' },
  { id: 'journal', label: 'Homeschool journal' },
  { id: 'tests', label: 'Tests and evaluations' },
  { id: 'trips', label: 'Field trips' },
  { id: 'samples', label: 'Work samples' },
];

export function PortfolioTab({ child }: { child: Child }) {
  const { toast, parent } = useParent();
  const [range, setRange] = useState<{ from: string; to: string } | null>(null);
  const [preview, setPreview] = useState<PortfolioPreview | null>(null);
  const [off, setOff] = useState<Set<PortfolioSection>>(new Set());
  const [grade, setGrade] = useState(child.grade ?? '');
  const [note, setNote] = useState('');
  const [signature, setSignature] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    const q = range ? `?from=${range.from}&to=${range.to}` : '';
    api
      .get<PortfolioPreview>(`/api/children/${child.id}/portfolio${q}`)
      .then((p) => {
        setPreview(p);
        if (!range) setRange({ from: p.from, to: p.to });
      })
      .catch((e) => toast({ text: (e as Error).message, tone: 'alert' }));
  }, [child.id, range, toast]);
  useEffect(load, [load]);

  if (!preview || !range) return <p className="muted">Loading…</p>;
  const available = SECTIONS.filter((s) => preview.sections[s.id] !== null);
  const chosen = available.filter((s) => !off.has(s.id)).map((s) => s.id);

  async function create() {
    if (!range) return;
    setBusy(true);
    try {
      const res = await fetch(`/api/children/${child.id}/portfolio`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tokens.parent()}` },
        body: JSON.stringify({ from: range.from, to: range.to, grade: grade.trim(), sections: chosen, note, signature }),
      });
      if (!res.ok) {
        const data = (await res.json().catch(() => null)) as { error?: string } | null;
        throw new ApiError(data?.error ?? `Couldn’t make the portfolio (${res.status}).`, res.status);
      }
      const name = /filename="([^"]+)"/.exec(res.headers.get('content-disposition') ?? '')?.[1] ?? 'portfolio.pdf';
      const url = URL.createObjectURL(await res.blob());
      const a = document.createElement('a');
      a.href = url;
      a.download = name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10_000);
      toast({ text: 'Your portfolio PDF is downloading.', tone: 'good' });
    } catch (e) {
      toast({ text: (e as Error).message, tone: 'alert' });
    } finally {
      setBusy(false);
    }
  }

  const st = preview.storage;
  return (
    <section className="card stack portfolio">
      <h2>{child.name}’s portfolio</h2>
      <p className="muted small">One PDF of {child.name}’s year, for your records, an evaluator or your accountability association.</p>
      <div className="field-row wrap-row">
        <label>
          From
          <input type="date" value={range.from} max={range.to} onChange={(e) => e.target.value && setRange({ ...range, from: e.target.value })} />
        </label>
        <label>
          To
          <input type="date" value={range.to} min={range.from} onChange={(e) => e.target.value && setRange({ ...range, to: e.target.value })} />
        </label>
        <label>
          Grade on the cover
          <GradeSelect value={grade} onChange={setGrade} />
        </label>
      </div>
      <div className="portfolio-sections" role="group" aria-label="Sections">
        {available.map((s) => (
          <label key={s.id} className="check portfolio-section">
            <input
              type="checkbox"
              checked={!off.has(s.id)}
              onChange={(e) =>
                setOff((cur) => {
                  const next = new Set(cur);
                  if (e.target.checked) next.delete(s.id);
                  else next.add(s.id);
                  return next;
                })
              }
            />
            <strong className="grow">{s.label}</strong>
            <span className="muted small">{preview.sections[s.id]}</span>
          </label>
        ))}
      </div>
      <label>
        A note from you <span className="muted small">(optional: how the year went, the curriculum you used)</span>
        <textarea rows={3} maxLength={1500} value={note} onChange={(e) => setNote(e.target.value)} />
      </label>
      <label className="check">
        <input type="checkbox" checked={signature} onChange={(e) => setSignature(e.target.checked)} />
        <span>
          Add a signature line: “I attest that these records are true and complete.” <span className="muted small">({parent.name}, parent)</span>
        </span>
      </label>
      <div className="row-gap wrap">
        <button className="btn primary" disabled={busy || !chosen.length} onClick={create}>
          <FileDown size={16} /> {busy ? 'Making the PDF…' : 'Create portfolio PDF'}
        </button>
        <span className="muted small">
          Work samples use {fileSize(st.usedBytes)} of your family’s {st.limitMb} MB.
        </span>
      </div>
      <p className="muted small">Add work samples on any assignment (open it from the Planner) or journal entry, and mark the ones to include.</p>
    </section>
  );
}
